import React, { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Search,
  Plus,
  Heart,
  Shield,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const HomepageAuth = ({ onClose, onSuccess, mode = null, title = null }) => {
  const { themeClasses } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
  });

  // Handle Escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onClose]);

  // Handle click outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isLogin) {
        // Login
        await signInWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
      } else {
        // Sign up
        if (formData.password !== formData.confirmPassword) {
          throw new Error("Passwords do not match");
        }
        if (formData.password.length < 6) {
          throw new Error("Password must be at least 6 characters");
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );

        // Update profile with display name
        if (formData.fullName) {
          await updateProfile(userCredential.user, {
            displayName: formData.fullName,
          });
        }
      }

      // Call onSuccess if provided (for modal flows), otherwise onClose (for normal auth)
      console.log(
        "HomepageAuth: Authentication successful, onSuccess provided:",
        !!onSuccess
      );
      if (onSuccess) {
        console.log("HomepageAuth: Calling onSuccess callback");
        onSuccess();
      } else {
        console.log("HomepageAuth: Calling onClose");
        onClose();
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // Call onSuccess if provided (for modal flows), otherwise onClose (for normal auth)
      console.log(
        "HomepageAuth Google: Authentication successful, onSuccess provided:",
        !!onSuccess
      );
      if (onSuccess) {
        console.log("HomepageAuth Google: Calling onSuccess callback");
        onSuccess();
      } else {
        console.log("HomepageAuth Google: Calling onClose");
        onClose();
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getModalConfig = () => {
    if (mode === "found-pet") {
      return {
        icon: Search,
        iconBg: "bg-green-500",
        iconColor: "text-white",
        title: isLogin
          ? "Sign in to Report Found Pet"
          : "Join to Report Found Pet",
        subtitle: isLogin
          ? "Help reunite pets with their families"
          : "Create an account to help lost pets find their way home",
        color: "green",
        gradientFrom: "from-green-500",
        gradientTo: "to-emerald-600",
      };
    } else {
      return {
        icon: Plus,
        iconBg: "bg-blue-500",
        iconColor: "text-white",
        title: isLogin ? "Sign in to Register Pet" : "Join to Register Pet",
        subtitle: isLogin
          ? "Protect your beloved companion with AI-powered identification"
          : "Create an account to keep your pet safe with advanced AI technology",
        color: "blue",
        gradientFrom: "from-blue-500",
        gradientTo: "to-purple-600",
      };
    }
  };

  const config = getModalConfig();
  const IconComponent = config.icon;

  return (
    <div
      className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-3xl w-full max-w-lg shadow-2xl relative overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-6 right-6 ${themeClasses.text.secondary} hover:${themeClasses.text.primary} p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 z-10`}
          aria-label="Close modal"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={`absolute -top-20 -right-20 w-40 h-40 ${config.iconBg} opacity-5 rounded-full blur-3xl`}
          ></div>
          <div
            className={`absolute -bottom-20 -left-20 w-40 h-40 ${config.iconBg} opacity-5 rounded-full blur-3xl`}
          ></div>
        </div>

        <div className="relative z-10 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className={`w-20 h-20 ${config.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}
            >
              <IconComponent className={`w-10 h-10 ${config.iconColor}`} />
            </div>
            <h2
              className={`text-3xl font-bold ${themeClasses.text.primary} mb-3`}
            >
              {title || config.title}
            </h2>
            <p
              className={`${themeClasses.text.secondary} text-lg leading-relaxed`}
            >
              {config.subtitle}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleEmailAuth} className="space-y-5 mb-6">
            {/* Full Name (Sign up only) */}
            {!isLogin && (
              <div>
                <label
                  className={`block text-sm font-semibold ${themeClasses.text.primary} mb-3`}
                >
                  Full Name
                </label>
                <div className="relative">
                  <User
                    className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeClasses.text.secondary}`}
                  />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full pl-12 pr-4 py-4 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} placeholder-gray-400 focus:border-${config.color}-500 focus:outline-none focus:ring-2 focus:ring-${config.color}-500/20 transition-all duration-200`}
                    placeholder="Enter your full name"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label
                className={`block text-sm font-semibold ${themeClasses.text.primary} mb-3`}
              >
                Email Address
              </label>
              <div className="relative">
                <Mail
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeClasses.text.secondary}`}
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-12 pr-4 py-4 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} placeholder-gray-400 focus:border-${config.color}-500 focus:outline-none focus:ring-2 focus:ring-${config.color}-500/20 transition-all duration-200`}
                  placeholder="Enter your email address"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                className={`block text-sm font-semibold ${themeClasses.text.primary} mb-3`}
              >
                Password
              </label>
              <div className="relative">
                <Lock
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeClasses.text.secondary}`}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-12 pr-14 py-4 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} placeholder-gray-400 focus:border-${config.color}-500 focus:outline-none focus:ring-2 focus:ring-${config.color}-500/20 transition-all duration-200`}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password (Sign up only) */}
            {!isLogin && (
              <div>
                <label
                  className={`block text-sm font-semibold ${themeClasses.text.primary} mb-3`}
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock
                    className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeClasses.text.secondary}`}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full pl-12 pr-4 py-4 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} placeholder-gray-400 focus:border-${config.color}-500 focus:outline-none focus:ring-2 focus:ring-${config.color}-500/20 transition-all duration-200`}
                    placeholder="Confirm your password"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r ${config.gradientFrom} ${config.gradientTo} hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg`}
            >
              {loading
                ? "Please wait..."
                : isLogin
                ? "Sign In & Continue"
                : "Create Account & Continue"}
            </button>
          </form>

          {/* Divider */}
          <div className="relative mb-6">
            <div className={`absolute inset-0 flex items-center`}>
              <div
                className={`w-full border-t ${themeClasses.border.primary}`}
              ></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span
                className={`${themeClasses.bg.card} px-4 ${themeClasses.text.secondary} font-medium`}
              >
                Or continue with
              </span>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleAuth}
            disabled={loading}
            className={`w-full ${themeClasses.bg.secondary} hover:opacity-90 border ${themeClasses.border.primary} ${themeClasses.text.primary} font-semibold py-4 px-6 rounded-xl transition-all duration-300 mb-6 flex items-center justify-center space-x-3 transform hover:scale-[1.02] shadow-sm`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC04"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>

          {/* Toggle between Login/Signup */}
          <div className="text-center">
            <p className={`${themeClasses.text.secondary} text-base`}>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError("");
                  setFormData({
                    email: "",
                    password: "",
                    confirmPassword: "",
                    fullName: "",
                  });
                }}
                className={`text-${config.color}-500 hover:text-${config.color}-600 ml-2 font-semibold transition-colors`}
              >
                {isLogin ? "Sign up here" : "Sign in here"}
              </button>
            </p>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>Secure & Private</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageAuth;
